import { addMethod, string } from "yup";
import { get, groupBy, isNil } from "lodash";

/**
 * Modifies a Yup schema in-place such that all fields have keys
 * equal to their keys in the schema object. The prefix is used
 * to handle potential key collision in nested objects.
 * @param {ObjectSchema} schema - Yup schema object
 * @param {String} prefix - level prefix
 * @return {ObjectSchema | undefined}
 */
export const addKeyToFieldsRecursively = (schema, prefix = "") => {
  if (!schema?.fields) {
    return;
  }

  Object.keys(schema.fields).forEach((fieldKey) => {
    const prefixedKey = prefix ? `${prefix}.${fieldKey}` : fieldKey;
    schema.fields[fieldKey].spec.key = prefixedKey;
    addKeyToFieldsRecursively(schema.fields[fieldKey], prefixedKey);
  });

  return schema;
};

// TODO: Move back to the table module. This is not useful for general Yup
//  operations, it is related to the table's internal logic.
export const getColumnDef = (schema, header, tooltip = header) => {
  schema.spec.label = header;
  return {
    id: schema.spec.key,
    header,
    accessorKey: schema.spec.key,
    schema,
    tooltip,
  };
};

export const validateWithContext = ({ schema, data, context }) => {
  return schema.validate(data, {
    context,
    abortEarly: false,
  });
};

export const hasNoData = (ctx) => {
  const allRows = ctx.options.context.allRows;
  return !allRows?.length;
};

addMethod(string, "unique", function () {
  return this.test("unique", "${label} must be unique", (value, ctx) => {
    const { schema, options } = ctx;
    const allRows = options.context.allRows;
    // Early exit for empty rows or nullable fields
    if (!allRows || !allRows.length || schema.spec.nullable === true) {
      return true;
    }

    const columnMap = options.context.columnMap;
    if (!columnMap.has(schema.spec.key)) {
      const rowsGroupedByValue = groupBy(allRows, (row) =>
        get(row, schema.spec.key)
      );
      columnMap.set(schema.spec.key, rowsGroupedByValue);
    }

    const hasCollision = columnMap.get(schema.spec.key)[value]?.length > 1;
    if (hasCollision) {
      return ctx.createError({
        path: options.key,
        params: { index: options.index },
      });
    }

    return true;
  });
});

addMethod(string, "requiredIfEmpty", function (otherField) {
  return this.test(
    "one-or-the-other",
    (prop) => `Either ${prop.spec.key} or ${otherField} must be provided`,
    function (value, ctx) {
      const otherValue = this.parent[otherField];
      return (
        (!isNil(otherValue) && otherValue.length > 0) ||
        (!isNil(value) && value.length > 0)
      );
    }
  );
});
